<template>
  <section class="modeling-clay-app">
    <div class="modeling-clay-app__padding">
      <div class="modeling-clay-app__stage">
        <section class="page-area noselect">
          <p class="page__title"><strong>Modeling a Head From Clay</strong></p>

          <!-- Intro -->
          <div class="page page--0" v-if="currentPage === 0">
            <div class="complete_model"></div>
            <div class="page__instructions">
              <p id="intro">Modeling is an <em>additive</em> process. <br>The artist builds up a soft, flexible material
                such as wax or
                clay, piece by piece. Both wax and clay may be used to make a mold for casting.</p>
            </div>
          </div>

          <!-- 1. Needs A Support -->
          <div class="page page--1" v-if="currentPage === 1">
            <div class="wrap">
              <div class="wrap__wire">
                <Container group-name="dndWire" @drop="dndWire" :should-animate-drop="()=>false">
                  <div class="hide_wire" v-if="!hasWireBeenUsed"></div>
                  <div class="show_wire" v-if="hasWireBeenUsed"></div>
                </Container>
              </div>
              <div class="wrap__stand">
                <Container group-name="dndStand" @drop="dndStand" :should-animate-drop="()=>false">
                  <div class="hide_board" v-if="!hasBoardBeenUsed"></div>
                  <div class="show_board" v-if="hasBoardBeenUsed"></div>
                </Container>
              </div>
              <div class="wrap__board">
                <Container group-name="dndBoard" @drop="dndBoard" :should-animate-drop="()=>false">
                  <div class="hide_stand" v-if="!hasStandBeenUsed"></div>
                  <div class="show_stand" v-if="hasStandBeenUsed"></div>
                </Container>
              </div>
            </div>
            <div class="base"></div>

            <div class="page__instructions">
              <p class="page__sub_title"><strong>needs a support</strong></p>
              <p>A clay sculpture needs a support, called an armature. It is attached by screws or wires to a wooden
                modeling board. Click and drag the pieces of the armature over to the modeling board.</p>
              <div class="tool-box2" style="display: flex; margin-bottom: 65px">
                <p>armature components</p>

                <!-- Board -->
                <div class="tool-box2__area-double__left-double">
                  <Container group-name="dndBoard" :drag-begin-delay="0">
                    <Draggable v-if="!hasBoardBeenUsed">
                      <div class="tool tool--board"></div>
                    </Draggable>
                  </Container>
                </div>

                <!-- Stand -->
                <div class="tool-box2__area-double__middle-double">
                  <Container group-name="dndStand" :drag-begin-delay="0">
                    <Draggable v-if="!hasStandBeenUsed">
                      <div class="tool tool--stand"></div>
                    </Draggable>
                  </Container>
                </div>

                <!-- Wire -->
                <div class="tool-box2__area-double__right-double">
                  <Container group-name="dndWire" :drag-begin-delay="0">
                    <Draggable v-if="!hasWireBeenUsed">
                      <div class="tool tool--wire"></div>
                    </Draggable>
                  </Container>
                </div>

              </div>
            </div>
          </div>

          <!-- 2. Building Up The Clay -->
          <div class="page page--2" v-if="currentPage === 2">
            <div class="wrap2">
              <div class="item">
                <Container group-name="dndFront" @drop="dndFront" :should-animate-drop="()=>false">
                  <div class="hide_front" v-if="!hasFrontBeenUsed"></div>
                  <div class="show_front" v-if="hasFrontBeenUsed"></div>
                </Container>
              </div>
              <div class="item">
                <Container group-name="dndBack" @drop="dndBack" :should-animate-drop="()=>false">
                  <div class="hide_back" v-if="!hasBackBeenUsed"></div>
                  <div class="show_back" v-if="hasBackBeenUsed"></div>
                </Container>
              </div>
              <div class="item">
                <Container group-name="dndNeck" @drop="dndNeck" :should-animate-drop="()=>false">
                  <div class="hide_neck" v-if="!hasNeckBeenUsed"></div>
                  <div class="show_neck" v-if="hasNeckBeenUsed"></div>
                </Container>
              </div>
              <div class="item">
                <Container group-name="dndEmpty" @drop="dndEmpty" :should-animate-drop="()=>false">
                  <div class="hide2" v-if="!hasNeckBeenUsed"></div>
                  <div class="hide2" v-if="hasNeckBeenUsed"></div>
                </Container>
              </div>
            </div>

            <div class="armature_0"></div>

            <div class="page__instructions">
              <p class="page__sub_title"><strong>Building Up The Clay</strong></p>
              <p>Drag the pieces of clay to the armature to begin the head. It's like making the skull of the head first
                - the bony structures of the front and back of the head and the neck.</p>
              <div class="tool-box2" style="display: flex;">
                <p>pieces of clay</p>

                <!-- Front of Head -->
                <div class="tool-box2__area-double__left-double2">
                  <Container group-name="dndFront" :drag-begin-delay="0">
                    <Draggable v-if="!hasFrontBeenUsed">
                      <div class="tool tool--front_of_head" @mouseenter="$event.currentTarget.classList.add('clicked')"
                        @mouseleave="$event.currentTarget.classList.remove('clicked')"
                        @touchstart="$event.currentTarget.classList.add('clicked')"
                        @touchend="$event.currentTarget.classList.remove('clicked')">
                      </div>
                      <div class="tool__text">Front of head</div>
                    </Draggable>
                  </Container>
                </div>

                <!-- Back of Head-->
                <div class="tool-box2__area-double__middle-double2">
                  <Container group-name="dndBack" :drag-begin-delay="0">
                    <Draggable v-if="!hasBackBeenUsed">
                      <div class="tool tool--back_of_head" @mouseenter="$event.currentTarget.classList.add('clicked')"
                        @mouseleave="$event.currentTarget.classList.remove('clicked')"
                        @touchstart="$event.currentTarget.classList.add('clicked')"
                        @touchend="$event.currentTarget.classList.remove('clicked')">
                      </div>
                      <div class="tool__text">Back of head</div>
                    </Draggable>
                  </Container>
                </div>

                <!-- Neck -->
                <div class="tool-box2__area-double__right-double2">
                  <Container group-name="dndNeck" :drag-begin-delay="0">
                    <Draggable v-if="!hasNeckBeenUsed">
                      <div class="tool tool--neck" @mouseenter="$event.currentTarget.classList.add('clicked')"
                        @mouseleave="$event.currentTarget.classList.remove('clicked')"
                        @touchstart="$event.currentTarget.classList.add('clicked')"
                        @touchend="$event.currentTarget.classList.remove('clicked')">
                      </div>
                      <div class="tool__text">Neck</div>
                    </Draggable>
                  </Container>
                </div>

              </div>
            </div>
          </div>

          <!-- 3. Begin To Add Features -->
          <div class="page page--3" v-show="currentPage === 3">
            {{ currentPage === 3 ? scene3Scrubbing() : ''}}
            <div class="interactive-box">
              <canvas style='position: absolute; top: 0; left: 0;' id="scene3BottomCanvas"></canvas>
              <canvas style='position: absolute; top: 0; left: 0;' id="scene3TopCanvas"></canvas>
              <canvas style='display: none;' id="scene3PatternCanvas"></canvas>
            </div>
            <div class="page__instructions">
              <p class="page__sub_title"><strong>Begin To Add Features</strong></p>
              <p>Draw the wooden modeling tool,<br> called a spatula, across the face to show where the eyes, nose,
                and<br>
                mouth will go.</p>
              <div class="tool-box">
                <div class="tool-box__area-single" style="display: flex;">

                  <div class="tool-box__area-single__left-single">
                    <p class="text-single">Wooden Spatula</p>
                  </div>

                  <div class="tool-box__area-single__right-single">
                    <vue-draggable-resizable @dragging="onDragging" @dragstop="onDragStop" id="scene3tool">
                      <div class="tool tool--spatula"></div>
                    </vue-draggable-resizable>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <!-- 4. Facial Features -->
          <div class="page page--4" v-if="currentPage === 4">
            <div class="wrap4">
              <div class="item4" id="hair">
                <Container group-name="dndHair" @drop="dndHair" :should-animate-drop="()=>false">
                  <div class="hide_hair" v-if="!hasHairToolBeenUsed"></div>
                  <div class="show_hair" v-if="hasHairToolBeenUsed"></div>
                </Container>
              </div>
              <div class="item4" id="eyes">
                <Container group-name="dndEyes" @drop="dndEyes" :should-animate-drop="()=>false">
                  <div class="armature_3" v-if="!hasEyesToolBeenUsed"></div>
                  <div class="armature_4" v-if="hasEyesToolBeenUsed"></div>
                </Container>
              </div>

              <div class="item4" id="ear">
                <Container group-name="dndEar" @drop="dndEar" :should-animate-drop="()=>false">
                  <div class="hide_ear" v-if="!hasEarToolBeenUsed"></div>
                  <div class="show_ear" v-if="hasEarToolBeenUsed"></div>
                </Container>
              </div>

              <div class="item4" id="nose">
                <Container group-name="dndNose" @drop="dndNose" :should-animate-drop="()=>false">
                  <div class="hide_nose" v-if="!hasNoseToolBeenUsed"></div>
                  <div class="show_nose" v-if="hasNoseToolBeenUsed"></div>
                </Container>
              </div>

              <div class="item4" id="mouth">
                <Container group-name="dndMouth" @drop="dndMouth" :should-animate-drop="()=>false">
                  <div class="hide_mouth" v-if="!hasMouthToolBeenUsed"></div>
                  <div class="show_mouth" v-if="hasMouthToolBeenUsed"></div>
                </Container>
              </div>

            </div>
            <div class="page__instructions">
              <p class="page__sub_title"><strong>Facial Features</strong></p>
              <p>Use the pieces of clay to add <br> dimension to the eyes, nose and <br> ears. Add the clay strip to
                form the hair.
              </p>
              <div class="tool-box-clear" style="display: flex;">

                <div class="tool-box-clear__area-twin">
                  <div class="tool-box-clear__area-twin__left">
                    <p>Pieces of Clay</p>
                    <div class="wrap3">
                      <div class="item2">
                        <Container group-name="dndEyes" :drag-begin-delay="0">
                          <Draggable v-if="!hasEyesToolBeenUsed">
                            <div class="tool tool--eyes" @mouseenter="$event.currentTarget.classList.add('clicked')"
                              @mouseleave="$event.currentTarget.classList.remove('clicked')"
                              @touchstart="$event.currentTarget.classList.add('clicked')"
                              @touchend="$event.currentTarget.classList.remove('clicked')">
                            </div>
                            <div class="tool__text-eyes">Eyes</div>
                          </Draggable>
                        </Container>
                      </div>

                      <div class="item2">
                        <Container group-name="dndNose" :drag-begin-delay="0">
                          <Draggable v-if="!hasNoseToolBeenUsed">
                            <div class="tool tool--nose" @mouseenter="$event.currentTarget.classList.add('clicked')"
                              @mouseleave="$event.currentTarget.classList.remove('clicked')"
                              @touchstart="$event.currentTarget.classList.add('clicked')"
                              @touchend="$event.currentTarget.classList.remove('clicked')">
                            </div>
                            <div class="tool__text-nose">Nose</div>
                          </Draggable>
                        </Container>
                      </div>

                      <div class="item2">
                        <Container group-name="dndMouth" :drag-begin-delay="0">
                          <Draggable v-if="!hasMouthToolBeenUsed">
                            <div class="tool tool--mouth" @mouseenter="$event.currentTarget.classList.add('clicked')"
                              @mouseleave="$event.currentTarget.classList.remove('clicked')"
                              @touchstart="$event.currentTarget.classList.add('clicked')"
                              @touchend="$event.currentTarget.classList.remove('clicked')">
                            </div>
                            <div class="tool__text-mouth">Mouth</div>
                          </Draggable>
                        </Container>
                      </div>

                      <div class="item2">
                        <Container group-name="dndEar" :drag-begin-delay="0">
                          <Draggable v-if="!hasEarToolBeenUsed">
                            <div class="tool tool--ear" @mouseenter="$event.currentTarget.classList.add('clicked')"
                              @mouseleave="$event.currentTarget.classList.remove('clicked')"
                              @touchstart="$event.currentTarget.classList.add('clicked')"
                              @touchend="$event.currentTarget.classList.remove('clicked')">
                            </div>
                            <div class="tool__text-ears">Ears</div>
                          </Draggable>
                        </Container>
                      </div>
                    </div>
                  </div>

                  <div class="tool-box-clear__area-twin__right">
                    <p>Strip of Clay</p>
                    <Container group-name="dndHair" :drag-begin-delay="0">
                      <Draggable v-if="!hasHairToolBeenUsed">
                        <div class="tool tool--hair" @mouseenter="$event.currentTarget.classList.add('clicked')"
                          @mouseleave="$event.currentTarget.classList.remove('clicked')"
                          @touchstart="$event.currentTarget.classList.add('clicked')"
                          @touchend="$event.currentTarget.classList.remove('clicked')">
                        </div>
                        <div class="tool__text-hair">Hair</div>
                      </Draggable>
                    </Container>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <!-- 5. Finishing Details -->
          <div class="page page--5" v-show="currentPage === 5">
            {{ currentPage === 5 ? scene5Scrubbing() : ''}}
            <div class="interactive-box">
              <canvas style='position: absolute; top: 0; left: 0;' id="scene5BottomCanvas"></canvas>
              <canvas style='position: absolute; top: 0; left: 0;' id="scene5TopCanvas"></canvas>
              <canvas style='display: none;' id="scene5PatternCanvas"></canvas>
            </div>

            <div class="page__instructions">
              <p class="page__sub_title"><strong>Finishing Details</strong></p>
              <p>To create the details of the features drag the wire-end modeling tool over the head. Now your clay head
                is <br> ready to become a mold for casting.</p>
              <div class="tool-box">
                <div class="tool-box__area-single" style="display: flex;">
                  <div class="tool-box__area-single__left-single">
                    <p class="text-single">wire-end modeling tool</p>
                  </div>
                  <div class="tool-box__area-single__right-single">
                    <vue-draggable-resizable @dragging="onDragging" @dragstop="onDragStop" id="scene5tool">
                      <div class="tool tool--wiretool"></div>
                    </vue-draggable-resizable>
                  </div>
                </div>
              </div>
              <a href="https://www.knowitall.org/series/sculpture">
                <div class="tool tool--stage_button"></div>
              </a>
            </div>
          </div>

          <!-- <div class="page page--6" v-show="currentPage === 6">
            page 6
          </div> -->

          <div class="modeling-clay-app__nav">
            <button v-show="currentPage > 0 && currentPage !== 5" @click="prevPage"
              class="i-btn i-btn--back">Back</button>
            <button v-if="currentPage < 5" @click="nextPage" class="i-btn i-btn--next">Next</button>
          </div>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
  import {
    Howl
  } from 'howler'

  import {
    Container,
    Draggable
  } from 'vue-smooth-dnd';

  export default {
    name: 'app',
    components: {
      Container,
      Draggable,
    },
    data: function () {
      return {
        isDragging: false,
        hasWireBeenUsed: false,
        hasBoardBeenUsed: false,
        hasStandBeenUsed: false,
        hasFrontBeenUsed: false,
        hasBackBeenUsed: false,
        hasNeckBeenUsed: false,
        hasEyesToolBeenUsed: false,
        hasMouthToolBeenUsed: false,
        hasNoseToolBeenUsed: false,
        hasEarToolBeenUsed: false,
        hasHairToolBeenUsed: false,
        currentPage: 0,
        currentSoundId: null,
        audio: new Howl({
          src: ['audio/modeling-sounds.mp3'],
          sprite: {
            click: [117, 69],
            pop: [849, 561],
            splat: [1655, 255]
          }
        }),
      }
    },
    mounted() {},
    computed: {},
    methods: {
      nextPage() {
        const pageBeforeLeaving = this.currentPage;
        if (this.currentPage < 5) {
          this.audio.play('click');
          this.currentPage++;
          this.resetTools(pageBeforeLeaving);
        }
      },
      prevPage() {
        const pageBeforeLeaving = this.currentPage;
        if (this.currentPage > 0) {
          this.audio.play('click');
          this.currentPage--;
          this.resetTools(pageBeforeLeaving);
        }
      },
      resetTools() {
        this.hasWireBeenUsed = false;
        this.hasBoardBeenUsed = false;
        this.hasStandBeenUsed = false;
        this.hasFrontBeenUsed = false;
        this.hasBackBeenUsed = false;
        this.hasNeckBeenUsed = false;
        this.hasEyesToolBeenUsed = false;
        this.hasMouthToolBeenUsed = false;
        this.hasNoseToolBeenUsed = false;
        this.hasEarToolBeenUsed = false;
        this.hasHairToolBeenUsed = false;
      },
      onDragging() {
        if (!this.isDragging) {
          this.isDragging = true;
        }
      },
      onDragStop() {
        if (this.isDragging) {
          this.isDragging = false;
        }
      },
      drawPattern(x, y, topCtx, bottomCtx, patternCanvas) {
        if (this.isDragging) {
          topCtx.fillStyle = bottomCtx.createPattern(patternCanvas, 'no-repeat');
          topCtx.beginPath();
          topCtx.arc(x, y, ((15 * window.innerWidth) / 725), 0, Math.PI * 2, true);
          topCtx.fill();
        }
      },

      scene3Scrubbing() {
        let bottomCanvas = document.getElementById('scene3BottomCanvas');
        let topCanvas = document.getElementById('scene3TopCanvas');
        let patternCanvas = document.getElementById('scene3PatternCanvas');

        const bottomCtx = bottomCanvas.getContext('2d');
        const mbCtx = patternCanvas.getContext('2d');

        const bottomImage = new Image();
        const topImage = new Image();

        bottomImage.onload = function () {
          bottomCanvas.width = bottomCanvas.clientWidth;
          bottomCanvas.height = bottomCanvas.clientHeight;
          bottomCtx.drawImage(bottomImage, 0, 0, bottomImage.width, bottomImage.height, 0, 0, bottomCanvas
            .clientWidth, bottomCanvas.clientHeight);
        };

        topImage.onload = function () {
          patternCanvas.width = bottomCanvas.clientWidth;
          patternCanvas.height = bottomCanvas.clientHeight;
          mbCtx.drawImage(topImage, 0, 0, topImage.width, topImage.height, 0, 0, bottomCanvas.clientWidth,
            bottomCanvas.clientHeight);
        };

        bottomImage.src = require('./assets/img/scene3--masked.svg');
        topImage.src = require('./assets/img/scene3--unmasked.svg');

        const tool = document.getElementById('scene3tool');
        const scene3Settings = {
          tool: tool,
          topCanvas: topCanvas,
          bottomCanvas: bottomCanvas,
          patternCanvas: patternCanvas,
          onMouseMove: this.drawPattern,
          preventDefault: true
        };

        this.setupMouse(scene3Settings);
      },

      scene5Scrubbing() {
        let bottomCanvas = document.getElementById('scene5BottomCanvas');
        let topCanvas = document.getElementById('scene5TopCanvas');
        let patternCanvas = document.getElementById('scene5PatternCanvas');

        const bottomCtx = bottomCanvas.getContext('2d');
        const mbCtx = patternCanvas.getContext('2d');

        const bottomImage = new Image();
        const topImage = new Image();

        bottomImage.onload = function () {
          bottomCanvas.width = bottomCanvas.clientWidth;
          bottomCanvas.height = bottomCanvas.clientHeight;
          bottomCtx.drawImage(bottomImage, 0, 0, bottomImage.width, bottomImage.height, 0, 0, bottomCanvas
            .clientWidth, bottomCanvas.clientHeight);
        };

        topImage.onload = function () {
          patternCanvas.width = bottomCanvas.clientWidth;
          patternCanvas.height = bottomCanvas.clientHeight;
          mbCtx.drawImage(topImage, 0, 0, topImage.width, topImage.height, 0, 0, bottomCanvas.clientWidth,
            bottomCanvas.clientHeight);
        };

        bottomImage.src = require('./assets/img/scene5--masked.svg');
        topImage.src = require('./assets/img/scene5--unmasked.svg');

        const tool = document.getElementById('scene5tool');
        const scene5Settings = {
          tool: tool,
          topCanvas: topCanvas,
          bottomCanvas: bottomCanvas,
          patternCanvas: patternCanvas,
          onMouseMove: this.drawPattern,
          preventDefault: true
        };

        this.setupMouse(scene5Settings);
      },
      setupMouse(settings) {
        let {
          topCanvas,
          bottomCanvas,
          patternCanvas,
          onMouseMove,
          preventDefault,
          tool,
        } = settings;

        let mouse = {
          x: 0,
          y: 0,
          down: false
        };

        let rectLeft, rectTop;
        const bottomCtx = bottomCanvas.getContext('2d');
        const topCtx = topCanvas.getContext('2d');
        const hook = topCanvas.addEventListener.bind(topCanvas);
        const mouseDown = updateMouseStatus.bind(null, true);
        const mouseUp = updateMouseStatus.bind(null, false);
        const toolHook = tool.addEventListener.bind(tool);

        toolHook('touchmove', updateCoordinates);
        hook('mousedown', mouseDown);
        hook('mouseup', mouseUp);
        hook('mousemove', updateCoordinates);
        hook('scroll', updateRect);

        function updateMouseStatus(b, e) {
          mouse.down = b;
          updateCoordinates(e);
          if (preventDefault) {
            e.stopPropagation();
            e.preventDefault();
          }
        }

        function updateCoordinates(e) {
          if (e.type === 'touchmove') {
            mouse.x = (Math.floor(e.touches[0].clientX) - rectLeft);
            mouse.y = (Math.floor(e.touches[0].clientY) - rectTop);
          } else {
            mouse.x = (e.clientX - rectLeft);
            mouse.y = (e.clientY - rectTop);
          }

          onMouseMove(mouse.x, mouse.y, topCtx, bottomCtx, patternCanvas);
          updateRect();
        }

        function updateRect() {
          const cWidth = topCanvas.clientWidth;
          const cHeight = topCanvas.clientHeight;

          if (topCanvas.width !== cWidth || topCanvas.height !== cHeight) {
            topCanvas.width = cWidth;
            topCanvas.height = cHeight;
          }

          const rect = topCanvas.getBoundingClientRect();
          rectLeft = Math.floor(rect.left);
          rectTop = Math.floor(rect.top);
        }

        updateRect();
      },

      dndWire(dropResult) {
        const {
          addedIndex
        } = dropResult;
        if (addedIndex !== null) {
          this.hasWireBeenUsed = true;
          this.audio.play('pop');
        }
      },

      dndBoard(dropResult) {
        const {
          addedIndex
        } = dropResult;
        if (addedIndex !== null) {
          this.hasBoardBeenUsed = true;
          this.audio.play('pop');
        }
      },

      dndStand(dropResult) {
        const {
          addedIndex
        } = dropResult;
        if (addedIndex !== null) {
          this.hasStandBeenUsed = true;
          this.audio.play('pop');
        }
      },

      dndFront(dropResult) {
        const {
          addedIndex
        } = dropResult;
        if (addedIndex !== null) {
          this.hasFrontBeenUsed = true;
          this.audio.play('splat');
        }
      },

      dndBack(dropResult) {
        const {
          addedIndex
        } = dropResult;
        if (addedIndex !== null) {
          this.hasBackBeenUsed = true;
          this.audio.play('splat');
        }
      },

      dndNeck(dropResult) {
        const {
          addedIndex
        } = dropResult;
        if (addedIndex !== null) {
          this.hasNeckBeenUsed = true;
          this.audio.play('splat');
        }
      },

      dndEmpty() {},

      dndEyes(dropResult) {
        const {
          addedIndex
        } = dropResult;
        if (addedIndex !== null) {
          this.hasEyesToolBeenUsed = true;
          this.audio.play('splat');
        }
      },

      dndMouth(dropResult) {
        const {
          addedIndex
        } = dropResult;
        if (addedIndex !== null) {
          this.hasMouthToolBeenUsed = true;
          this.audio.play('splat');
        }
      },

      dndNose(dropResult) {
        const {
          addedIndex
        } = dropResult;
        if (addedIndex !== null) {
          this.hasNoseToolBeenUsed = true;
          this.audio.play('splat');
        }
      },

      dndEar(dropResult) {
        const {
          addedIndex
        } = dropResult;
        if (addedIndex !== null) {
          this.hasEarToolBeenUsed = true;
          this.audio.play('splat');
        }
      },

      dndHair(dropResult) {
        const {
          addedIndex
        } = dropResult;
        if (addedIndex !== null) {
          this.hasHairToolBeenUsed = true;
          this.audio.play('splat');
        }
      },
    }
  }
</script>

<style lang="scss">
  @import "style";

  .noselect {
    -webkit-touch-callout: none;
    /* iOS Safari */
    -webkit-user-select: none;
    /* Safari */
    -khtml-user-select: none;
    /* Konqueror HTML */
    -moz-user-select: none;
    /* Old versions of Firefox */
    -ms-user-select: none;
    /* Internet Explorer/Edge */
    user-select: none;
    /* Non-prefixed version, currently supported by Chrome, Opera and Firefox */
  }

  .hide-tool {
    z-index: -1;
  }

  //   body{
  //   overflow: hidden;
  // }

  .smooth-dnd-container {
    .animated {
      transform: none !important;
    }
  }

  .modeling-clay-app {
    background: $yellow;
    margin: 0;
    padding: 0;

    &__nav {
      position: absolute;
      right: fluid(110px);
      bottom: fluid(-25px);
      width: fluid(180px);

      button {
        font-family: $cursive;
        text-transform: uppercase;
        cursor: pointer;
      }
    }

    &__padding {
      position: relative;
      padding: 0 0 #{(380/725)*100%};
    }

    &__stage {
      padding: fluid(16px);
      overflow: hidden;
      position: absolute;
      width: 100%;
      height: 100%;
    }
  }

  .page-area {
    width: 100%;
    position: relative;
    margin: auto;
  }

  .page {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    max-width: fluid(550px);
    max-height: fluid(251px);
    margin: auto;

    &__title {
      font-family: $cursive;
      font-weight: 300;
      color: lighten(#605D22, 0%);
      text-shadow: fluid(1px) fluid(1px) fluid(.5px) black;
      text-align: center;
      margin: 0 auto;
      margin-bottom: fluid(20px);
      font-size: fluid(44px);
    }

    &__sub_title {
      text-align: left;
      width: 100%;
      text-transform: uppercase;
      color: #000;
      font-size: fluid(15px);
      text-transform: uppercase;
      margin: 0;
      padding: fluid(3px) fluid(0px);
    }

    &__instructions {
      flex: 0 0 auto;
      padding: fluid(0px) fluid(5px);
      text-align: left;
      color: #000;
      font-size: fluid(14px);
      margin-bottom: fluid(35px);
      word-wrap: none;
      width: fluid(260px);

      #intro {
        position: relative;
        margin: fluid(8px) auto;
        font-size: fluid(16px);
        padding: fluid(20px) fluid(0px);
        left: fluid(-90px);
      }
    }
  }

  .wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: auto;
    width: fluid(243px);
    height: fluid(251px);
    z-index: 8;

    &__wire {
      height: 33.33%;
      width: 100%;
      z-index: 16;
      // background-color: #FF000099;
    }

    &__stand {
      height: 33.33%;
      width: 100%;
      // background-color: #00FF0099;
    }

    &__board {
      height: 33.33%;
      width: 100%;
      // background-color: #0000FF99;
    }
  }

  .wrap2 {
    display: flex;
    position: absolute;
    left: fluid(65px);
    flex-wrap: wrap;
    justify-content: space-between;
    width: fluid(243px);
    height: fluid(251px);
    // z-index: 8;
  }

  .item {
    width: 53%;
    height: 50%;
    margin: auto;
    // background-color: #ff000099;
  }

  .item:nth-child(2n) {
    width: 47%;
    height: 50%;
    // background-color: #00ff0099;
  }

  .item:nth-child(3n) {
    width: 100%;
    height: 28%;
    // background-color: #0000ff99;
  }

  .item:nth-child(4n) {
    width: 100%;
    height: 22%;
    // background-color: #ffff0099;
  }

  .wrap3 {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 75%;
    height: fluid(45px);
    margin: auto;
    // z-index: 23;
  }

  .item2 {
    width: 100%;
    height: 30%;
    // background-color: #ff000099;
  }

  .item2:nth-child(2n) {
    width: 33.3%;
    height: 100%;
    // background-color: #00ff0099;
  }

  .item2:nth-child(3n) {
    width: 33.3%;
    height: 100%;
    // background-color: #0000ff99;

  }

  .item2:nth-child(4n) {
    width: 33.3%;
    height: 100%;
    // background-color: #ffff0099;

  }

  .wrap4 {
    width: fluid(243px);
    height: fluid(251px);
    // background: #5b53cf99;
  }

  #hair {
    position: relative;
    top: fluid(-10px);
    height: 15%;
    width: 75%;
    // background-color: #ff000099;
    z-index: 12;
    margin: 0 auto;
  }

  #eyes {
    position: relative;
    height: 15%;
    // background-color: #0000ff99; 
  }

  #nose {
    position: absolute;
    margin-top: fluid(20px);
    top: fluid(117px);
    left: fluid(120px);
    height: fluid(60px);
    width: 11%;
    // background-color: #00ffff99;
  }

  #mouth {
    position: absolute;
    margin-top: fluid(20px);
    top: fluid(136px);
    left: fluid(120px);
    height: fluid(35px);
    width: 15%;
    // background-color: #ffff0099;
  }

  #ear {
    position: relative;
    top: fluid(-30px);
    right: fluid(-125px);
    height: 30%;
    width: 30%;
    // background-color: #ff00ff99;

  }

  .complete_model {
    position: relative;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene5--unmasked.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    width: fluid(397px);
    height: fluid(255px);
    left: fluid(-90px);
  }

  .smooth-dnd-container {
    position: relative;
    height: 100%;
    width: 100%;
    z-index: 10;
  }

  .base {
    position: absolute;
    background-image: url('~@/assets/img/scene1--base.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    bottom: fluid(-15px);
    left: fluid(52px);
    width: fluid(246px);
    height: fluid(99px);
    z-index: 7;
  }

  .show_board {
    position: absolute;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene1--l_board.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    top: fluid(95px);
    left: fluid(13px);
    width: fluid(190px);
    height: fluid(60px);
    z-index: 10;
    // opacity: 1;
  }

  .hide_board {
    position: absolute;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene1--l_board.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    top: fluid(110px);
    left: fluid(13px);
    width: fluid(216px);
    height: fluid(60px);
    z-index: 10;
    opacity: 0;
  }

  .show_stand {
    position: absolute;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene1--l_stand.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    top: fluid(-83px);
    left: fluid(98.5px);
    width: fluid(29px);
    height: fluid(120px);
    z-index: 11;
  }

  .hide_stand {
    position: absolute;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene1--l_stand.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    top: fluid(-83px);
    left: fluid(108.5px);
    width: fluid(32px);
    height: fluid(135px);
    z-index: 11;
    display: none;
  }

  .show_wire {
    position: absolute;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene1--l_wire.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center bottom;
    top: fluid(10px);
    left: fluid(-11px);
    width: fluid(244px);
    height: fluid(90px);
    z-index: 12;
  }

  .hide_wire {
    position: relative;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene1--l_wire.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center bottom;
    top: fluid(1px);
    left: fluid(2px);
    width: fluid(244px);
    height: fluid(100px);
    z-index: 12;
    display: none;
  }

  .show_front {
    position: absolute;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene2--armature_front.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    top: fluid(-5px);
    left: fluid(38px);
    width: fluid(100px);
    height: fluid(150px);
    z-index: 18;
  }

  .hide_front {
    display: none;
    z-index: 18;
  }

  .show_back {
    position: absolute;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene2--armature_back.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    top: fluid(-5px);
    left: fluid(-58px);
    width: fluid(100px);
    height: fluid(147px);
    z-index: 17;
  }

  .hide_back {
    display: none;
    z-index: 17;
  }

  .show_neck {
    position: relative;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene2--armature_neck.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    top: fluid(-53px);
    left: fluid(80px);
    width: fluid(80px);
    height: fluid(90px);
    z-index: 16;
  }

  .hide_neck {
    display: none;
    z-index: 16;
  }

  .show_mouth {
    position: absolute;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene4--lmouth.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    top: fluid(24px);
    left: fluid(8px);
    width: fluid(33px);
    height: fluid(23px);
    z-index: 12;
  }

  .hide_mouth {
    z-index: 12;
    display: none;
  }

  .show_nose {
    position: absolute;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene4--lnose.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    top: fluid(24px);
    left: fluid(8px);
    width: fluid(20px);
    height: fluid(19px);
    z-index: 1;
  }

  .hide_nose {
    z-index: 12;
    display: none;
  }

  .show_hair {
    position: absolute;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene4--lhair.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    top: fluid(20px);
    left: fluid(43px);
    width: fluid(72px);
    height: fluid(20px);
    z-index: 12;
  }

  .hide_hair {
    z-index: 12;
    display: none;
  }

  .show_ear {
    position: absolute;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene4--lear2.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    top: fluid(18px);
    left: fluid(3px);
    width: fluid(37px);
    height: fluid(40px);
    z-index: 12;
  }

  .hide_ear {
    z-index: 12;
    display: none;
  }

  .armature_0 {
    position: relative;
    flex: 0 0 auto;
    background: url('~@/assets/img/scene2--armature_0.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center bottom;
    width: fluid(243px);
    height: fluid(251px);
    left: fluid(-14px);
    // margin: auto;
  }

  .armature_1 {
    flex: 0 0 auto;
    background: url('~@/assets/img/scene2--armature_full.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center bottom;
    width: fluid(243px);
    height: fluid(251px);
  }

  .armature_3 {
    position: absolute;
    top: fluid(-40px);
    flex: 0 0 auto;
    background: url('~@/assets/img/scene4--feature_base.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center center;
    width: fluid(224px);
    height: fluid(251px);
    z-index: 0;
  }

  .armature_4 {
    position: absolute;
    top: fluid(-40px);
    flex: 0 0 auto;
    background: url('~@/assets/img/scene4--eye_drop2.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center center;
    width: fluid(224px);
    height: fluid(251px);
    z-index: -1;
  }

  .tool-box-clear {
    width: fluid(260px);
    height: fluid(110px);
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
    margin: auto;


    &__area-twin {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      height: 90%;
      margin: auto;

      &__left,
      &__right {
        position: relative;
        width: 46%;
        height: 100%;
        border-radius: fluid(14px);
        background: #9E9B5C;
        box-shadow: fluid(-1.5px) fluid(3px) 0px 0px rgba(0, 0, 0, 0.5);
        margin: auto;
      }
    }

    p {
      font-family: $cursive;
      width: 100%;
      padding: fluid(5px) fluid(0px);
      text-align: center;
      word-spacing: fluid(2px);
      text-transform: uppercase;
      color: #fff;
      font-size: fluid(16px);
      margin: 0;
    }
  }

  .tool-box {
    width: fluid(207px);
    height: fluid(75px);
    // left: fluid(50px);
    // margin: 0 auto;
    border-radius: fluid(14px);
    display: flex;
    flex-flow: row wrap;
    position: relative;
    background: #9E9B5C;
    box-shadow: fluid(-3px) fluid(3px) 0px 0px rgba(0, 0, 0, 0.5);
    // z-index: 99;


    p {
      font-family: $cursive;
      width: 90%;
      padding: fluid(3px) fluid(0px);
      text-align: center;
      text-transform: uppercase;
      color: #fff;
      font-size: fluid(18px);
      margin: 0 auto;
    }

    &__area {
      display: flex;
      position: absolute;
      flex-flow: row wrap;
      justify-content: space-around;
      margin: auto;
      top: 5%;
      width: 100%;

      &__left {
        width: 50%;
        height: 100%;
        color: white;
        font-family: $cursive;
        text-align: center;

        .base_text {
          position: relative;
          width: 100px;
          left: fluid(-10px);
          padding: 0% 50%;
          font-size: fluid(16px);
          text-transform: lowercase;
          line-height: fluid(14px);
        }
      }

      &__right {
        width: 50%;
        height: 100%;
        color: white;
        font-family: $cursive;
        text-align: center;

        .base_text {
          position: relative;
          left: fluid(-10px);
          padding: 0% 50%;

          font-size: fluid(16px);
          text-transform: lowercase;
          line-height: fluid(14px);
        }
      }
    }

    &__area-single {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;

      &__left-single {
        width: 50%;
        margin: auto;

        .text-single {
          font-family: $cursive;
          width: 100%;
          padding: fluid(0px) fluid(0px);
          text-align: center;
          color: #fff;
          font-size: fluid(18px);
          line-height: fluid(24px);
          margin-left: fluid(0px);
          text-transform: uppercase;
        }
      }

      &__right-single {
        width: 50%;
      }
    }

    &__area-double {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;

      &__left-double {
        margin: auto;
        padding-left: 10px;
        width: 50%;
        height: 70%;
      }

      &__middle-double {
        margin: auto;
        width: 25%;
        height: 70%;
      }

      &__right-double {
        margin: auto;
        width: 25%;
        height: 70%;
      }

      &__left-double2 {
        margin: auto;
        // padding-left: 10px;
        width: 30%;
        height: 70%;
      }

      &__middle-double2 {
        margin: auto;
        width: 36%;
        height: 70%;
      }

      &__right-double2 {
        margin: auto;
        width: 33%;
        height: 70%;
      }

      .text-double {
        font-family: $cursive;
        width: 60%;
        padding: fluid(0px) fluid(0px);
        text-align: center;
        color: #fff;
        font-size: fluid(14px);
        line-height: fluid(14px);
        margin: auto;
        text-transform: capitalize;
      }
    }
  }

  .tool-box2 {
    width: fluid(236px);
    height: fluid(95px);
    // left: fluid(50px);
    // margin: 0 auto;
    border-radius: fluid(14px);
    display: flex;
    flex-flow: row wrap;
    position: relative;
    background: #9E9B5C;
    box-shadow: fluid(-3px) fluid(3px) 0px 0px rgba(0, 0, 0, 0.5);
    z-index: 99;


    p {
      font-family: $cursive;
      width: 90%;
      padding: fluid(3px) fluid(0px);
      text-align: center;
      text-transform: uppercase;
      color: #fff;
      font-size: fluid(18px);
      margin: 0 auto;
    }

    &__area {
      display: flex;
      position: absolute;
      flex-flow: row wrap;
      justify-content: space-around;
      margin: auto;
      top: 5%;
      width: 100%;

      &__left {
        width: 50%;
        height: 100%;
        color: white;
        font-family: $cursive;
        text-align: center;

        .base_text {
          position: relative;
          width: 100px;
          left: fluid(-10px);
          padding: 0% 50%;
          font-size: fluid(16px);
          text-transform: lowercase;
          line-height: fluid(14px);
        }
      }

      &__right {
        width: 50%;
        height: 100%;
        color: white;
        font-family: $cursive;
        text-align: center;

        .base_text {
          position: relative;
          left: fluid(-10px);
          padding: 0% 50%;

          font-size: fluid(16px);
          text-transform: lowercase;
          line-height: fluid(14px);
        }
      }
    }

    &__area-single {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;

      &__left-single {
        width: 50%;
        margin: auto;

        .text-single {
          font-family: $cursive;
          width: 100%;
          padding: fluid(0px) fluid(0px);
          text-align: center;
          color: #fff;
          font-size: fluid(14px);
          line-height: fluid(14px);
          margin-left: fluid(0px);
          text-transform: uppercase;
        }
      }

      &__right-single {
        width: 50%;
      }
    }

    &__area-double {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;

      &__left-double {
        margin: auto;
        padding-left: 10px;
        width: 50%;
        height: 70%;
      }

      &__middle-double {
        margin: auto;
        width: 25%;
        height: 70%;
      }

      &__right-double {
        margin: auto;
        width: 25%;
        height: 70%;
      }

      &__left-double2 {
        margin: auto;
        width: 30%;
        height: 70%;
      }

      &__middle-double2 {
        margin: auto;
        width: 36%;
        height: 70%;
      }

      &__right-double2 {
        margin: auto;
        width: 33%;
        height: 70%;
      }

      .text-double {
        font-family: $cursive;
        width: 60%;
        padding: fluid(0px) fluid(0px);
        text-align: center;
        color: #fff;
        font-size: fluid(14px);
        line-height: fluid(14px);
        margin: auto;
        text-transform: capitalize;
      }
    }
  }

  .tool {
    background-repeat: no-repeat;
    background-size: contain;
    cursor: pointer;

    &.clicked {
      &+.tool__text {
        display: block;
        text-align: center;
        width: 100%;
        margin: 0 auto;
        font-size: fluid(10px);
        font-weight: 800;
        background: #FFFFCC;
        border: 1px solid #000;
        margin: fluid(0px) fluid(0px);
      }

      &+.tool__text-eyes {
        position: relative;
        display: block;
        text-align: center;
        width: 50%;
        margin: 0 auto;
        font-size: fluid(10px);
        font-weight: 800;
        background: #FFFFCC;
        border: 1px solid #000;
        margin: fluid(0px) fluid(0px);
        left: fluid(85px);
      }

      &+.tool__text-ears {
        position: relative;
        display: block;
        text-align: center;
        width: 100%;
        margin: 0 auto;
        font-size: fluid(10px);
        font-weight: 800;
        background: #FFFFCC;
        border: 1px solid #000;
        margin: fluid(0px) fluid(0px);
        left: fluid(30px);
        top: fluid(17px);
      }

      &+.tool__text-mouth {
        position: relative;
        display: block;
        width: fluid(40px);
        margin: 0 auto;
        font-size: fluid(10px);
        font-weight: 800;
        background: #FFFFCC;
        border: 1px solid #000;
        margin: fluid(0px) fluid(20px);
        padding: fluid(0px) fluid(5px);
        left: fluid(-30px);
        top: fluid(50px);
      }

      &+.tool__text-nose {
        position: relative;
        display: block;
        text-align: center;
        width: fluid(35px);
        margin: 0 auto;
        font-size: fluid(10px);
        font-weight: 800;
        background: #FFFFCC;
        border: 1px solid #000;
        margin: fluid(0px) fluid(0px);
        padding: fluid(0px) fluid(0px);
        right: fluid(35px);
        top: fluid(12px);
      }

      &+.tool__text-hair {
        position: relative;
        display: block;
        text-align: center;
        width: fluid(35px);
        margin: 0 auto;
        font-size: fluid(10px);
        font-weight: 800;
        background: #FFFFCC;
        border: 1px solid #000;
        margin: fluid(0px) fluid(0px);
        left: fluid(115px);
        top: fluid(14px);
      }
    }

    &__text {
      display: none;
    }

    &__text-eyes,
    &__text-ears,
    &__text-mouth,
    &__text-nose,
    &__text-hair {
      display: none;
    }

    &--combo {
      display: flex;
    }

    &--board {
      position: relative;
      background-image: url('~@/assets/img/scene1--board.svg');
      margin: 25% auto;
      height: fluid(25px);
      width: fluid(104px);
      background-position: bottom;
      background-repeat: no-repeat;
      background-size: contain;
      z-index: 2;
    }

    &--stand {
      position: relative;
      background-image: url('~@/assets/img/scene1--stand.svg');
      margin: auto;
      height: fluid(55px);
      @include image;
      z-index: 2;
    }

    &--wire {
      position: relative;
      background-image: url('~@/assets/img/scene1--wire.svg');
      margin: auto;
      height: fluid(55px);
      @include image;
      z-index: 2;
    }

    &--front_of_head {
      position: relative;
      background-image: url('~@/assets/img/scene2--front_of_head.svg');
      margin: auto;
      height: fluid(60px);
      width: fluid(100px);
      left: fluid(-10px);
      @include image;
      z-index: 2;
    }

    &--back_of_head {
      position: relative;
      background-image: url('~@/assets/img/scene2--back_of_head.svg');
      margin: auto;
      height: fluid(60px);
      width: fluid(100px);
      @include image;
      z-index: 2;
    }

    &--neck {
      position: relative;
      background-image: url('~@/assets/img/scene2--neck.svg');
      margin: 10% auto;
      height: fluid(40px);
      width: fluid(50px);
      @include image;
      z-index: 2;
    }

    &--spatula {
      position: relative;
      background-image: url('~@/assets/img/scene3--spatula.svg');
      width: fluid(85px);
      height: fluid(85px);
      top: fluid(15px);
      left: fluid(105px);
      z-index: 2;
    }

    &--eyes {
      position: absolute;
      background-image: url('~@/assets/img/scene4--eyes.svg');
      width: fluid(70px);
      height: fluid(20px);
      top: fluid(0px);
      left: fluid(10px);
      z-index: 12;
    }

    &--ear {
      position: absolute;
      background-image: url('~@/assets/img/scene4--ear.svg');
      width: fluid(45px);
      height: fluid(20px);
      top: fluid(15px);
      left: fluid(2px);
      z-index: 12;
    }

    &--mouth {
      position: absolute;
      background-image: url('~@/assets/img/scene4--mouth.svg');
      width: fluid(50px);
      height: fluid(13px);
      top: fluid(32px);
      left: fluid(0px);
      z-index: 12;
    }

    &--nose {
      position: absolute;
      background-image: url('~@/assets/img/scene4--nose.svg');
      width: fluid(13px);
      height: fluid(13px);
      top: fluid(15px);
      left: fluid(5px);
      z-index: 12;
    }

    &--hair {
      position: absolute;
      background-image: url('~@/assets/img/scene4--hair.svg');
      width: fluid(95px);
      height: fluid(20px);
      top: fluid(17px);
      right: fluid(10px);
      z-index: 12;
    }

    &--wiretool {
      position: absolute;
      background-image: url('~@/assets/img/scene5--wiretool.svg');
      width: fluid(85px);
      height: fluid(65px);
      top: fluid(5px);
      left: fluid(105px);
      z-index: 2;
    }

    &--stage_button {
      display: block;
      position: relative;
      margin: fluid(0px) fluid(40px);
      background-image: url("~@/assets/img/button_back.svg");
      width: fluid(136px);
      height: fluid(48px);
      top: fluid(30px);
    }
  }

  .draggable {
    width: auto !important;
    height: auto !important;
  }

  .interactive-box {
    position: relative;
    // border: 3px solid red;
    width: fluid(224px);
    height: fluid(251px);

    canvas {
      width: 100%;
      height: 100%;
    }
  }

  .i-btn {
    appearance: none;
    border: 0;
    background: transparent;
    font-weight: 600;
    margin: auto;
    font-size: fluid(14px);
    flex: 0 0 auto;

    &:before {
      content: '';
      width: fluid(25px);
      height: fluid(25px);
      background-position: center center;
      background-repeat: no-repeat;
      background-size: contain;
      display: block;
      margin: 0 auto;
    }

    &--back {
      float: left;
      justify-self: start;

      &:before {
        background-image: url('~@/assets/img/button_arrow_l.svg');
      }
    }

    &--next {
      float: right;

      &:before {
        background-image: url('~@/assets/img/button_arrow_r.svg');
      }
    }
  }
</style>